/* eslint-disable react/no-unknown-property */
import { useThree } from '@react-three/fiber'
import React from 'react'
import * as THREE from 'three'
import { Reflector } from 'three/examples/jsm/objects/Reflector'

import {
  type ReflectorRegistration,
  hasCoarsePointer,
  prepareReflector,
  useReflectorGate,
  useReflectorRegistration,
} from './reflector-gate'

const DEFAULT_MAX_TEXTURE_SIZE = 1024

export interface PlanarReflectorProps {
  node: THREE.Mesh
  resolution?: number
  color?: string
  facingThreshold?: number
  clipBias?: number
  multisample?: number
  maxTextureSize?: number
  flipNormal?: boolean
  updateInterval?: number
  centerCtrl?: [number, number, number] | []
  rotationCtrl?: [number, number, number] | []
  shape?: 'rectangle' | 'rounded' | 'circle'
  cornerRadius?: number
}

export interface PlanarReflectorControl extends PlanarReflectorProps {
  enabled?: boolean
}

function PlanarReflector({
  node,
  resolution = 1024,
  color = '#888888',
  facingThreshold = 0,
  clipBias = 0.02,
  multisample,
  maxTextureSize,
  flipNormal = false,
  updateInterval = 1,
  centerCtrl = [],
  rotationCtrl = [],
  shape = 'rectangle',
  cornerRadius = 0.1,
}: PlanarReflectorProps) {
  const gl = useThree((state) => state.gl)
  const gate = useReflectorGate()

  const updateIntervalRef = React.useRef(updateInterval)
  const facingThresholdRef = React.useRef(facingThreshold)

  React.useEffect(() => {
    updateIntervalRef.current = updateInterval
    facingThresholdRef.current = facingThreshold
  }, [updateInterval, facingThreshold])

  const built = React.useMemo(() => {
    const { geometry } = node
    geometry.computeBoundingBox()
    const bb = geometry.boundingBox ?? new THREE.Box3()

    const baseCenter = new THREE.Vector3()
    bb.getCenter(baseCenter)

    const size = new THREE.Vector3()
    bb.getSize(size)

    const axes = [
      { dir: new THREE.Vector3(1, 0, 0), len: size.x },
      { dir: new THREE.Vector3(0, 1, 0), len: size.y },
      { dir: new THREE.Vector3(0, 0, 1), len: size.z },
    ].sort((a, b) => a.len - b.len)

    const normalAxis = axes[0].dir.clone()
    const faceNormal = new THREE.Vector3(0, 0, 1)
    const normalAttr = geometry.attributes.normal
    if (normalAttr) {
      faceNormal
        .fromBufferAttribute(normalAttr as THREE.BufferAttribute, 0)
        .normalize()
    }
    if (normalAxis.dot(faceNormal) < 0) normalAxis.negate()
    if (flipNormal) normalAxis.negate()

    const widthAxis = axes[2].dir.clone()
    const heightAxis = new THREE.Vector3()
      .crossVectors(normalAxis, widthAxis)
      .normalize()

    widthAxis.crossVectors(heightAxis, normalAxis).normalize()

    const baseOrientation = new THREE.Quaternion().setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(widthAxis, heightAxis, normalAxis)
    )

    const w = axes[2].len
    const h = axes[1].len

    let planeGeometry: THREE.BufferGeometry

    if (shape === 'circle') {
      planeGeometry = new THREE.CircleGeometry(Math.min(w, h) / 2, 64)
    } else if (shape === 'rounded') {
      const r = Math.min(cornerRadius, w / 1.5, h / 1.5)
      const s = new THREE.Shape()
      const x = -w / 2
      const y = -h / 2
      s.moveTo(x + r, y)
      s.lineTo(x + w - r, y)
      s.quadraticCurveTo(x + w, y, x + w, y + r)
      s.lineTo(x + w, y + h - r)
      s.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
      s.lineTo(x + r, y + h)
      s.quadraticCurveTo(x, y + h, x, y + h - r)
      s.lineTo(x, y + r)
      s.quadraticCurveTo(x, y, x + r, y)
      planeGeometry = new THREE.ShapeGeometry(s)
    } else {
      planeGeometry = new THREE.PlaneGeometry(w, h)
    }

    const dpr = gl.getPixelRatio()
    const cap = Math.min(
      Math.max(maxTextureSize ?? DEFAULT_MAX_TEXTURE_SIZE, resolution),
      gl.capabilities.maxTextureSize
    )
    const textureSize = Math.round(Math.min(resolution * dpr, cap))
    const samples = Math.min(
      multisample ?? (hasCoarsePointer() ? 0 : 4),
      gate.options.maxMultisample
    )

    const nextReflector = new Reflector(planeGeometry, {
      color: new THREE.Color(color),
      textureWidth: textureSize,
      textureHeight: textureSize,
      clipBias,
      multisample: 4,
    })

    prepareReflector(nextReflector, color)

    return {
      reflector: nextReflector,
      textureSize,
      samples,
      localCenter: baseCenter,
      orientation: baseOrientation,
    }
  }, [
    node,
    resolution,
    color,
    gl,
    clipBias,
    multisample,
    maxTextureSize,
    flipNormal,
    gate,
    shape,
    cornerRadius,
  ])
  const { reflector, textureSize, samples, localCenter, orientation } = built

  const center = React.useMemo<[number, number, number]>(() => {
    const [ctrlX, ctrlY, ctrlZ] = centerCtrl
    return [
      localCenter.x + (ctrlX || 0),
      localCenter.y + (ctrlY || 0),
      localCenter.z + (ctrlZ || 0),
    ]
  }, [localCenter, centerCtrl])

  const quaternion = React.useMemo<[number, number, number, number]>(() => {
    const [pitch, yaw, roll] = rotationCtrl

    const delta = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(
        THREE.MathUtils.degToRad(pitch || 0),
        THREE.MathUtils.degToRad(yaw || 0),
        THREE.MathUtils.degToRad(roll || 0),
        'YXZ'
      )
    )
    const quat = orientation.clone().multiply(delta)
    return [quat.x, quat.y, quat.z, quat.w]
  }, [orientation, rotationCtrl])

  const registration = React.useMemo<ReflectorRegistration>(() => {
    reflector.geometry.computeBoundingBox()
    return {
      localBounds: reflector.geometry.boundingBox ?? new THREE.Box3(),
      textureSize,
      samples,
      fallbackColor: color,
      getFacingThreshold: () => facingThresholdRef.current,
      getUpdateInterval: () => updateIntervalRef.current,
    }
  }, [reflector, textureSize, samples, color])

  useReflectorRegistration(reflector, registration)

  React.useEffect(
    () => () => {
      reflector.getRenderTarget().dispose()
      reflector.dispose()
      reflector.geometry.dispose()
    },
    [reflector]
  )

  return (
    <group position={node.position} rotation={node.rotation} scale={node.scale}>
      <primitive object={reflector} position={center} quaternion={quaternion} />
    </group>
  )
}

export default PlanarReflector
